import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChevronLeft, Loader } from "lucide-react";
import { RequestFormStepProps, RequestTimeData, RequestTimeDataValue, TimeoffCategoryType } from "../../../../types";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { addDays } from "date-fns";
import { DateTimeUtil } from "../../../../utils";
import { toast } from "react-toastify";

const FormStep: React.FC<RequestFormStepProps> = ({
    formData,
    setFormData,
    selectedDates,
    onBack,
    onSubmit,
    isUpdateMode,
    timeoffToUpdate,
}) => {
    const { isCreatingTimeoffRequest, isUpdatingTimeoffRequest } = useSelector((state: RootState) => state.timeoff);
    const generateDateRange = (start: Date, end: Date): Date[] => {
        const dates: Date[] = [];
        let current = start;
        while (current <= end) {
            dates.push(new Date(current));
            current = addDays(current, 1);
        }
        return dates;
    };

    const fullDateRange = generateDateRange(selectedDates[0], selectedDates[selectedDates.length - 1]);

    const [timeData, setTimeData] = useState<RequestTimeData>(() =>
        fullDateRange.reduce((acc, date) => {
            const dateMysql = DateTimeUtil.formatDateToMySQL(date);
            acc[dateMysql] = {
                start_time: "",
                end_time: "",
                amount: "0",
                date: dateMysql,
                use_time_range: false,
            };
            return acc;
        }, {} as RequestTimeData)
    );

    const timeoffCategories = useSelector((state: RootState) => state.common.assignedTimeoffCategories);
    const selectedCategory: TimeoffCategoryType | undefined = useMemo(
        () => timeoffCategories.find((cat) => cat.category_code === formData.category_code),
        [formData.category_code, timeoffCategories]
    );
    const isTimeRangeOnlyCategory = selectedCategory?.other_details_json?.REQUEST_ENTRY_MODE === "TIME_RANGE_ONLY";

    useEffect(() => {
        if (!selectedCategory?.track_time_in || selectedCategory.track_time_in !== "Hours") {
            return;
        }

        setTimeData((prev) =>
            Object.fromEntries(
                Object.entries(prev).map(([key, val]) => [
                    key,
                    isTimeRangeOnlyCategory
                        ? { ...val, use_time_range: true }
                        : { ...val, use_time_range: false, start_time: "", end_time: "" },
                ])
            )
        );
    }, [isTimeRangeOnlyCategory, selectedCategory?.track_time_in]);

    const totalTime = useMemo(() => {
        return Object.values(timeData)
            .reduce((acc, item) => acc + parseFloat(item.amount || "0"), 0) // Convert amount to number
            .toString(); // Convert back to string if needed for UI
    }, [timeData]);

    const totalTimeDisplay = useMemo(() => {
        const n = Math.round(Number(totalTime) * 100) / 100;
        return n % 1 === 0 ? String(n) : n.toFixed(2);
    }, [totalTime]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name === "category_code") {
            // Reset all time values when category changes
            setTimeData((prev) =>
                Object.fromEntries(
                    Object.entries(prev).map(([key, val]) => [
                        key,
                        {
                            ...val,
                            amount: "0",
                            start_time: "",
                            end_time: "",
                            use_time_range: false,
                        },
                    ])
                )
            );
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        if (!formData.category_code) {
            // alert("Please select a category.");
            toast.error("Please select a category. If you cannot find the category, please contact your manager.", {
                position: "bottom-center",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            return;
        }

        if (!totalTime || Number(totalTime) === 0) {
            toast.error("Please enter a valid total time.", {
                position: "bottom-center",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            return;
        }

        const detailsArray = Object.entries(timeData).map(([date, { start_time, end_time, amount, use_time_range }]) => {
            const durationType =
                selectedCategory?.track_time_in === "Hours"
                    ? ((isTimeRangeOnlyCategory || use_time_range) ? "TIME_RANGE" : "HOURS")
                    : selectedCategory?.track_time_in === "Days"
                      ? (amount === "1" ? "FULL_DAY" : "HALF_DAY")
                      : "HOURS";

            const baseDetail = {
                date,
                amount,
                duration_type: durationType,
            };

            return (durationType === "HOURS"
                ? baseDetail
                : { ...baseDetail, start_time: start_time || null, end_time: end_time || null }) as RequestTimeDataValue;
        });

        setFormData((prev) => ({ ...prev, details: detailsArray as RequestTimeDataValue[] }));
        onSubmit({ ...formData, details: detailsArray });
    };

    const deriveAmountFromTimeRange = (start?: string | null, end?: string | null) => {
        if (!start || !end) {
            return { amount: "0", invalidRange: false };
        }

        const parseTime = (time: string) => {
            const [hoursStr, minutesStr] = time.split(":");
            const hours = Number(hoursStr);
            const minutes = Number(minutesStr);

            if (
                Number.isNaN(hours) ||
                Number.isNaN(minutes) ||
                hours < 0 ||
                hours > 23 ||
                minutes < 0 ||
                minutes > 59
            ) {
                return null;
            }

            return hours * 60 + minutes;
        };

        const startMinutes = parseTime(start);
        const endMinutes = parseTime(end);

        if (startMinutes === null || endMinutes === null) {
            return { amount: "0", invalidRange: false };
        }

        const diffMinutes = endMinutes - startMinutes;

        if (diffMinutes <= 0) {
            return { amount: "0", invalidRange: true };
        }

        const hours = diffMinutes / 60;
        const formatted = Number.isInteger(hours)
            ? hours.toString()
            : hours.toFixed(2).replace(/\.?0+$/, "");

        return { amount: formatted, invalidRange: false };
    };

    const handleTimeChange = (dateKey: string, field: "start_time" | "end_time", value: string) => {
        setTimeData((prev) => {
            const existing =
                prev[dateKey] ?? { date: dateKey, start_time: "", end_time: "", amount: "0", use_time_range: false };
            const updated = { ...existing, [field]: value };
            const { amount, invalidRange } = deriveAmountFromTimeRange(updated.start_time, updated.end_time);

            if (invalidRange) {
                toast.error("End time must be after start time.", {
                    position: "bottom-center",
                    autoClose: 3000,
                });
            }

            return {
                ...prev,
                [dateKey]: {
                    ...updated,
                    amount,
                },
            };
        });
    };

    const handleTimeRangeToggle = (dateKey: string, useRange: boolean) => {
        if (isTimeRangeOnlyCategory && !useRange) {
            return;
        }

        setTimeData((prev) => {
            const existing =
                prev[dateKey] ?? { date: dateKey, start_time: "", end_time: "", amount: "0", use_time_range: false };

            if (useRange) {
                const { amount } = deriveAmountFromTimeRange(existing.start_time, existing.end_time);
                return {
                    ...prev,
                    [dateKey]: {
                        ...existing,
                        use_time_range: true,
                        amount,
                    },
                };
            }

            return {
                ...prev,
                [dateKey]: {
                    ...existing,
                    use_time_range: false,
                    start_time: "",
                    end_time: "",
                },
            };
        });
    };

    const handleDurationChange = (dateKey: string, value: string) => {
        // If the input is empty, reset to "0"
        if (value === "") {
            setTimeData((prev) => ({
                ...prev,
                [dateKey]: { ...prev[dateKey], amount: "0" },
            }));
            return;
        }

        // Remove leading zeros if present
        let sanitizedValue = value.replace(/^0+(\d)/, "$1");

        // Ensure it is a valid numeric input
        if (/^\d*\.?\d*$/.test(sanitizedValue)) {
            const numericValue = parseFloat(sanitizedValue);

            // Ensure it's not more than 24
            if (numericValue > 24) return;
            setTimeData((prev) => ({
                ...prev,
                [dateKey]: { ...prev[dateKey], amount: sanitizedValue },
            }));
        }
    };

    return (
        <div>
            {/* <div className="relative flex items-center justify-center mb-4">
                <button
                    aria-label="Back"
                    className="text-gray-700 hover:text-black absolute left-4"
                    onClick={onBack}
                >
                    <ArrowLeft className="w-6 h-6" />
                </button>
                <h2 className="text-xl font-semibold">New Request</h2>
            </div> */}

            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-800 mb-2">
                    <span className="text-red-500 text-lg">*</span> Category
                </label>
                <select
                    name="category_code"
                    value={formData.category_code || ""}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white appearance-none"
                >
                    <option value="">--Select--</option>
                    {timeoffCategories.map((category) => (
                        <option key={category.category_code} value={category.category_code}>
                            {category.category_name}
                        </option>
                    ))}
                </select>
                {/* {!formData.category_code && <p className="text-red-500 text-sm mt-1">Category is required.</p>} */}
            </div>

            {selectedCategory?.track_time_in && (
                <div>
                    <div className="flex justify-between items-center text-gray-700 font-medium mb-2 text-sm">
                        <span>
                            <span className="text-red-500 text-lg">*</span> {selectedCategory?.track_time_in ?? ""}
                        </span>
                        <span>Total: {totalTimeDisplay}</span>
                    </div>

                    <div className="border border-gray-200 rounded-md">
                        {fullDateRange.map((date) => {
                            const dateKey = DateTimeUtil.formatDateToMySQL(date);
                            const entry = timeData[dateKey];
                            const useTimeRange = isTimeRangeOnlyCategory || (entry?.use_time_range ?? false);

                            return (
                                <div
                                    key={dateKey}
                                    className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between px-3 py-3 border-b border-gray-200"
                                >
                                    <span className="text-md font-medium text-gray-700">
                                        {DateTimeUtil.formatShortDate(date)}
                                    </span>

                                    {selectedCategory?.track_time_in === "Hours" ? (
                                        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                                            {!isTimeRangeOnlyCategory && (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-gray-600 whitespace-nowrap">
                                                        Use time range
                                                    </span>
                                                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only peer"
                                                            checked={useTimeRange}
                                                            onChange={(e) => handleTimeRangeToggle(dateKey, e.target.checked)}
                                                        />
                                                        <div className="relative w-10 h-5 bg-gray-300 rounded-full peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-400 peer-checked:bg-gray-800 transition-colors">
                                                            <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                                                        </div>
                                                    </label>
                                                </div>
                                            )}
                                            {useTimeRange ? (
                                                <div className="flex flex-wrap items-center gap-3">
                                                    <label className="text-sm text-gray-600" htmlFor={`start-${dateKey}`}>
                                                        Start
                                                    </label>
                                                    <input
                                                        id={`start-${dateKey}`}
                                                        type="time"
                                                        value={entry?.start_time || ""}
                                                        onChange={(e) => handleTimeChange(dateKey, "start_time", e.target.value)}
                                                        className="border border-gray-300 rounded-md px-2 py-1 text-center bg-white"
                                                        step={900}
                                                    />
                                                    <label className="text-sm text-gray-600" htmlFor={`end-${dateKey}`}>
                                                        End
                                                    </label>
                                                    <input
                                                        id={`end-${dateKey}`}
                                                        type="time"
                                                        value={entry?.end_time || ""}
                                                        onChange={(e) => handleTimeChange(dateKey, "end_time", e.target.value)}
                                                        className="border border-gray-300 rounded-md px-2 py-1 text-center bg-white"
                                                        step={900}
                                                    />
                                                </div>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={entry?.amount?.toString() ?? "0"}
                                                    onChange={(e) => handleDurationChange(dateKey, e.target.value)}
                                                    className="border border-gray-300 rounded-md px-2 py-1 w-20 text-center bg-white"
                                                    inputMode="decimal"
                                                />
                                            )}
                                            <span className="text-sm text-gray-600 sm:ml-4">
                                                {entry?.amount || "0"} hrs
                                            </span>
                                        </div>
                                    ) : (
                                        <select
                                            value={timeData[dateKey]?.amount.toString()}
                                            onChange={(e) => handleDurationChange(dateKey, e.target.value)}
                                            className="border border-gray-300 rounded-md px-2 py-1 w-20 text-center bg-white appearance-none"
                                        >
                                            <option value="">--Select--</option>
                                            <option value="1">Full</option>
                                            <option value="0.5">Half</option>
                                        </select>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="mt-4">
                <label className="block text-sm font-medium text-gray-800 mb-2">Note</label>
                <textarea
                    name="note"
                    value={formData.note || ""}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
                    placeholder="Add a note (optional)"
                />
            </div>

            {isCreatingTimeoffRequest || isUpdatingTimeoffRequest ? (
                <div className="flex justify-center items-center">
                    <Loader className="animate-spin w-6 h-6 text-gray-800" />
                </div>
            ) : (
                <button
                    className="w-full px-4 py-2 mt-4 bg-gray-800 text-white rounded-md hover:bg-gray-900"
                    onClick={handleSubmit}
                >
                    Submit
                </button>
            )}
        </div>
    );
};

export default FormStep;
